import Loading from '@/common/components/atom/loading';
import { LayoutWithHeaderAndFooter } from '@/common/components/layouts/layoutWithHeaderAndFooter';
import Seo from '@/common/components/layouts/seo';
import classNames from '@/common/utils/classNames';
import {
  getCallIdFromQuery,
  getHamiChatIdFromAppRoute,
  getHamiChatIdFromPath,
  getHamiPathFromUrl,
  HAMI_ORIGIN,
  isHamiChatDetailFromAppRoute,
  isHamiChatDetailUrl,
  isHamiMainChatsUrl,
  toAppChatsRouteFromHamiPath,
  toHamiIframeSrc,
  toHamiPathFromAppRoute,
  withCallParam,
} from '@/modules/hami/chatsRouting';
import { ChatAssistantDrawer } from '@/modules/hami/components/chatAssistantDrawer';
import { ChatAssistantPanel } from '@/modules/hami/components/chatAssistantPanel';
import { ChatRecoveryDock } from '@/modules/hami/chatRecovery/RecoveryDock';
import { ChatsSkeleton } from '@/modules/hami/components/chatsSkeleton';
import { SAFE_AREA_INSETS_MESSAGE, SAFE_AREA_INSETS_SUPPORTED_MESSAGE } from '@/modules/hami/safeAreaInsets';
import { getFloatingNavInset } from '@/common/components/layouts/bottomNavigation/floatingBottomNavigation';
import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

/** The old way: the standalone Hami site in an iframe. Kept as a fallback behind ?legacy=1. */
export const LegacyIframeChatsPage = (props: any) => {
  const router = useRouter();
  // The route is now pages/_/chats/[[...slug]] (it was [[...hami]]); the old name stays readable for old links.
  const hamiRoute = router.query.slug ?? router.query.hami;
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [showBottomNavigation, setShowBottomNavigation] = useState(true);
  const [isChatDetail, setIsChatDetail] = useState(false);
  const [iframeChatId, setIframeChatId] = useState<string | null>(null);
  const [iframeSrc, setIframeSrc] = useState(`${HAMI_ORIGIN}/`);
  const [recoveryMaskOn, setRecoveryMaskOn] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // New app: tell Hami how much the floating nav covers; go full height once Hami handles it.
  const isFloatingNav = useIsNewAppShell();
  const [hamiHandlesInsets, setHamiHandlesInsets] = useState(false);
  const bottomInset = isFloatingNav && showBottomNavigation ? getFloatingNavInset() : 0;
  const bottomInsetRef = useRef(bottomInset);
  bottomInsetRef.current = bottomInset;
  const postInsets = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: SAFE_AREA_INSETS_MESSAGE, bottom: bottomInsetRef.current },
      HAMI_ORIGIN,
    );
  }, []);
  useEffect(postInsets, [bottomInset, postInsets]);
  const lastSyncedRouteRef = useRef<string | null>(null);
  const syncFromIframeRef = useRef(false);
  // ماسکِ chat-recovery (مالک ۲۰۲۶-۱۰-۱۱): وقتی پیشنهاد جایگزینی زنده است، کلیک
  // روی لینک‌های saga داخل پیام‌های hami باعث ناوبریِ iframe می‌شود (پلکِ لوتولی +
  // ری‌لودِ hami). چون iframe کراس‌اورجین است URLش خوانده نمی‌شود؛ فقط onLoad دارد —
  // همان لحظه overlay می‌کشیم و وقتی hami دوباره URLش را post کرد برمی‌داریم.
  const recoveryInteractiveRef = useRef(false);
  const iframeBootedRef = useRef(false);
  const recoveryMaskTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const disarmRecoveryMask = useCallback(() => {
    if (recoveryMaskTimerRef.current) {
      clearTimeout(recoveryMaskTimerRef.current);
      recoveryMaskTimerRef.current = null;
    }
    setRecoveryMaskOn(false);
  }, []);

  const armRecoveryMask = useCallback(() => {
    if (recoveryMaskTimerRef.current) clearTimeout(recoveryMaskTimerRef.current);
    setRecoveryMaskOn(true);
    // safety: اگر hami به هر دلیلی URLش را post نکرد، پرده تا ابد نمی‌ماند
    recoveryMaskTimerRef.current = setTimeout(() => {
      recoveryMaskTimerRef.current = null;
      setRecoveryMaskOn(false);
    }, 8000);
  }, []);

  const handleRecoveryInteractive = useCallback((interactive: boolean) => {
    recoveryInteractiveRef.current = interactive;
    if (!interactive) disarmRecoveryMask();
  }, [disarmRecoveryMask]);

  useEffect(() => () => {
    if (recoveryMaskTimerRef.current) clearTimeout(recoveryMaskTimerRef.current);
  }, []);

  // Only sync iframe src on first load / parent navigation (back, refresh, shared link).
  // Hami is SPA: internal navigations must not change iframe src.
  useEffect(() => {
    if (!router.isReady) return;

    const hamiPath = withCallParam(toHamiPathFromAppRoute(hamiRoute), getCallIdFromQuery(router.query.call));
    const inChatDetail = isHamiChatDetailFromAppRoute(hamiRoute);
    setShowBottomNavigation(isHamiMainChatsUrl(hamiPath));
    setIsChatDetail(inChatDetail);
    setIframeChatId(inChatDetail ? getHamiChatIdFromAppRoute(hamiRoute) : null);

    if (syncFromIframeRef.current) {
      syncFromIframeRef.current = false;
      return;
    }

    const nextSrc = toHamiIframeSrc(hamiPath);
    setIframeSrc(prev => (prev === nextSrc ? prev : nextSrc));
  }, [router.isReady, router.asPath]);

  useEffect(() => {
    // اسکلت تا رسیدنِ حامی (onLoad) می‌ماند؛ این فقط سقفِ ایمنی است اگر onLoad هیچ‌وقت نیاید
    const timeout = setTimeout(() => setIsAppLoading(false), 15000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!router.isReady) return;

    const handleMessage = (event: MessageEvent) => {
      // صفحات خودِ اپ (مثل runner) که در iframe لود می‌شوند هم می‌توانند ماسک را بردارند —
      // فقط lift؛ route sync همچنان انحصاریِ hami است.
      if (event.origin === window.location.origin) {
        if (event.data?.type === 'CHAT_RECOVERY_MASK_LIFT') disarmRecoveryMask();
        return;
      }
      if (event.origin !== HAMI_ORIGIN) return;
      if (event.data?.type === SAFE_AREA_INSETS_SUPPORTED_MESSAGE) {
        setHamiHandlesInsets(true);
        return;
      }
      if (event.data?.type !== 'IFRAME_URL_CHANGED' || !event.data?.url) return;

      // hami زنده است و مسیرش را گزارش کرد — هر ماسکی که به‌خاطر ناوبریِ saga بود برداشته می‌شود
      disarmRecoveryMask();

      const nextHamiPath = getHamiPathFromUrl(event.data.url);
      if (!nextHamiPath) return;

      const normalized = nextHamiPath.startsWith('/') ? nextHamiPath : `/${nextHamiPath}`;
      const inChatDetail = isHamiChatDetailUrl(normalized);
      setShowBottomNavigation(isHamiMainChatsUrl(normalized));
      setIsChatDetail(inChatDetail);
      setIframeChatId(inChatDetail ? getHamiChatIdFromPath(normalized) : null);

      const nextRoute = toAppChatsRouteFromHamiPath(normalized);
      if (lastSyncedRouteRef.current === nextRoute) return;

      const currentRoute = router.asPath.split('?')[0];
      if (currentRoute === nextRoute) {
        lastSyncedRouteRef.current = nextRoute;
        return;
      }

      lastSyncedRouteRef.current = nextRoute;
      syncFromIframeRef.current = true;
      router.replace(nextRoute, undefined, { shallow: true, scroll: false });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [router, disarmRecoveryMask]);

  useEffect(() => {
    if (!router.isReady) return;
    lastSyncedRouteRef.current = router.asPath.split('?')[0];
  }, [router.isReady, router.asPath]);

  const routeChatId = router.isReady ? getHamiChatIdFromAppRoute(hamiRoute) : null;
  const chatId = routeChatId ?? iframeChatId;
  const isVardastActive = isChatDetail && !isAppLoading;

  const chatIframe = (
    <iframe
      ref={iframeRef}
      onLoad={() => {
        setIsAppLoading(false);
        postInsets();
        // ناوبریِ iframe وقتی پیشنهاد جایگزینی زنده است = کلیک روی لینک saga در
        // پیام‌ها — پلکِ لوتولی و ری‌لودِ hami پشت overlay پوشانده می‌شود.
        // بارِ اولِ بوت iframe ماسک نمی‌گیرد (recovery هنوز fetch نشده).
        if (iframeBootedRef.current && recoveryInteractiveRef.current) {
          armRecoveryMask();
        }
        iframeBootedRef.current = true;
      }}
      className={classNames('h-full w-full flex-grow', { hidden: isAppLoading })}
      allow="microphone; camera; fullscreen; clipboard-write;"
      sandbox="allow-forms allow-modals allow-downloads allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-top-navigation allow-top-navigation-by-user-activation allow-top-navigation-to-custom-protocols allow-storage-access-by-user-activation"
      src={iframeSrc}
    />
  );

  return (
    <LayoutWithHeaderAndFooter
      {...props.config}
      shouldShowPromoteApp={false}
      showFooter={false}
      showHeader={false}
      showBottomNavigation={showBottomNavigation}
      fullBleedUnderBottomNav={hamiHandlesInsets}
      className="!h-svh !min-h-svh !max-h-svh:"
    >
      <Seo title={'گفت‌وگو‌ها'} noIndex />
      <div className="relative flex md:h-[calc(100vh-80px)] h-full w-full flex-grow flex-col items-center justify-center overflow-y-auto">
        {isAppLoading && <ChatsSkeleton chatDetail={isChatDetail} />}

        <ChatAssistantDrawer
          isActive={isVardastActive}
          chatId={chatId}
          panelContent={({ isOpen, hasChatWidget, isWidgetsLoading }) => (
            <ChatAssistantPanel
              isOpen={isOpen}
              chatId={chatId}
              hasChatWidget={hasChatWidget}
              isWidgetsLoading={isWidgetsLoading}
            />
          )}
        >
          {chatIframe}
        </ChatAssistantDrawer>
        <ChatRecoveryDock chatId={chatId} active={isVardastActive} onInteractiveChange={handleRecoveryInteractive} />
        {recoveryMaskOn && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/95">
            <Loading />
            <span className="mt-3 px-6 text-center text-[13px] font-medium leading-7 text-[#525252]">
              در حال انجام درخواست شما؛ نتیجه را همین‌جا اعلام می‌کنیم…
            </span>
          </div>
        )}
      </div>
    </LayoutWithHeaderAndFooter>
  );
};

export default LegacyIframeChatsPage;
